import React, { useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography,
  Chip,
  Button,
  LinearProgress,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import PaidIcon from '@mui/icons-material/Paid';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import { Screen } from 'src/components/screen';

const AnalyticsScreen: React.FC = () => {
  const metrics = useMemo(
    () => [
      {
        title: 'Sales (30d)',
        value: '$482k',
        delta: '+8.4%',
        caption: 'Toast + Square combined',
        icon: <TrendingUpIcon color="success" />,
      },
      {
        title: 'Expense burn',
        value: '$192k',
        delta: '-3.1%',
        caption: 'vs prior 30d',
        icon: <PaidIcon color="warning" />,
      },
      {
        title: 'Analytics coverage',
        value: '92%',
        delta: 'Live',
        caption: 'Data freshness across sources',
        icon: <AnalyticsIcon color="primary" />,
      },
    ],
    [],
  );

  const chartBars = [
    { label: 'Week 1', value: 68 },
    { label: 'Week 2', value: 74 },
    { label: 'Week 3', value: 82 },
    { label: 'Week 4', value: 90 },
  ];

  return (
    <Screen contentContainerStyle={{ flex: 1, display: 'flex', padding: 16 }}>
      <Box sx={{ width: '100%', maxWidth: 1100, mx: 'auto' }}>
        <Grid container spacing={2} mb={3}>
          {metrics.map(card => (
            <Grid item xs={12} md={4} key={card.title}>
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  borderRadius: 3,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    bgcolor: theme => theme.palette.action.hover,
                    display: 'grid',
                    placeItems: 'center',
                  }}>
                  {card.icon}
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    {card.title}
                  </Typography>
                  <Typography variant="h5" fontWeight={700}>
                    {card.value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {card.caption} · <Chip label={card.delta} size="small" />
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                border: theme => `1px solid ${theme.palette.divider}`,
              }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                <ShowChartIcon color="primary" />
                <Typography variant="h6">Sales & expense trend</Typography>
              </Stack>
              <Stack spacing={1.5} mt={1}>
                {chartBars.map(bar => (
                  <Stack key={bar.label} spacing={0.25}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center">
                      <Typography variant="body2">{bar.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {bar.value}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={bar.value}
                      color={bar.value > 80 ? 'success' : 'primary'}
                      sx={{ borderRadius: 999 }}
                    />
                  </Stack>
                ))}
              </Stack>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                border: theme => `1px solid ${theme.palette.divider}`,
              }}>
              <Typography variant="h6" mb={1}>
                Custom dashboards
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Pin sales, expense, and review widgets by location or by brand.
              </Typography>
              <Stack spacing={1}>
                <Button variant="contained" size="small">
                  Create dashboard
                </Button>
                <Button variant="text" size="small">
                  Customize widgets
                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Screen>
  );
};

export { AnalyticsScreen };
